import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import {
  NAVIGATION_ITEM_FLAT_TREE_CONFIG,
  NavigationItemFlatTreeConfig
} from '@/app/platform/platform-shared/navigation-item-tree/types';
import { NavigationItemTreeComponent } from '@/app/platform/platform-shared/navigation-item-tree/navigation-item-tree-component';
import { LAYOUT_ACTIONS, LayoutActions } from '@/app/core/store/types';
import { PlatformAppContext } from '@/app/platform/platform-context/platform-app-context';
import { UserNavigationItem } from '@/app/platform/platform-core/user/models/user-navigation-item';
import { UserEgiApp } from '@/app/platform/platform-core/user/models/user-egi-app';
import { Router } from '@angular/router';
import { LocationStrategy } from '@angular/common';
import { EgiAppData } from '@/app/platform/platform-shared/models/types';
import { Subscription } from 'rxjs';
import { unsubscribeAll } from '@common/utils/rxjs-utils';
import { ApplicationService } from '@/app/application.service';
import { isInTrustedDomain } from '@common/utils/security.utils';

@Component({
    selector: 'egi-page-menu',
    templateUrl: './egi-page-menu.component.html',
    styleUrls: ['./egi-page-menu.component.scss'],
    standalone: false
})
export class EgiPageMenuComponent
  extends NavigationItemTreeComponent<UserNavigationItem, EgiAppData>
  implements OnInit, OnDestroy
{
  constructor(
    public readonly egiAppContext: PlatformAppContext,
    @Inject(NAVIGATION_ITEM_FLAT_TREE_CONFIG)
    private readonly config: NavigationItemFlatTreeConfig<UserNavigationItem>,
    @Inject(LAYOUT_ACTIONS)
    private readonly layoutActions: LayoutActions,
    private readonly router: Router,
    public readonly appService: ApplicationService,
    private readonly cdr: ChangeDetectorRef,
    private readonly locationStrategy: LocationStrategy
  ) {
    super(
      config,
      node => node.id + this.app.id,
      () => this.treeControl.collapseAll(),
      () => false
    );
  }

  private readonly subscriptions: Subscription[] = [];
  /** Link per menu-item (id + route); de template vraagt hem bij elke change detection op. */
  private readonly hrefCache = new Map<string, string | undefined>();
  private readonly dataSourceReady$ = new BehaviorSubject<boolean>(false);
  private ancestorIds = new Set<string>();

  protected fillDataSource(app: EgiAppData) {
    this.hrefCache.clear();
    this.dataSource.data = app.rootNavigationItem.clone().children;
    setTimeout(() => this.dataSourceReady$.next(true));
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.egiAppContext.activeApp$.subscribe(
        a => (this.appInput = a ?? UserEgiApp.createDefault())
      )
    );
    this.subscriptions.push(
      combineLatest([this.egiAppContext.activeNavigationItem$, this.dataSourceReady$]).subscribe(
        ([activeItem, ready]) => {
          if (!ready) {
            return;
          }
          this.applyActiveItem(activeItem);
        }
      )
    );
    this.treeControl.collapseAll();
  }

  isAncestorOfActive(item: UserNavigationItem): boolean {
    return !!item && this.ancestorIds.has(item.id);
  }

  private applyActiveItem(activeItem: UserNavigationItem | undefined): void {
    this.ancestorIds = new Set<string>();
    if (!activeItem) {
      this.cdr.markForCheck();
      return;
    }
    const nodes = this.treeControl.dataNodes ?? [];
    if (!nodes.length) {
      return;
    }
    let parentId = activeItem.parentId;
    const guard = new Set<string>();
    while (parentId && !guard.has(parentId)) {
      guard.add(parentId);
      const parentNode = nodes.find(n => n.navigationItem.id === parentId);
      if (!parentNode) {
        break;
      }
      this.ancestorIds.add(parentNode.navigationItem.id);
      this.treeControl.expand(parentNode);
      parentId = parentNode.navigationItem.parentId;
    }
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    unsubscribeAll(this.subscriptions);
  }

  trackBy = (index: number, item: UserNavigationItem) => item.id;

  closeSideNav() {
    this.layoutActions.closeSideBar();
  }

  /**
   * Link van een pagina-item voor het label (rechtermuisklik → "Openen in
   * nieuw tabblad" van de browser); menu-knopen en niet-toegankelijke items
   * krijgen er geen.
   */
  linkHref(navigationItem: UserNavigationItem): string | undefined {
    const cacheKey = `${navigationItem.id}|${navigationItem.routerLink ?? ''}`;
    if (!this.hrefCache.has(cacheKey)) {
      this.hrefCache.set(cacheKey, this.canOpenInNewTab(navigationItem) ? this.newTabUrl(navigationItem) : undefined);
    }
    return this.hrefCache.get(cacheKey);
  }

  /** Enkel echte, toegankelijke pagina's (geen menu-knopen) krijgen het nieuw-tabblad-icoon. */
  canOpenInNewTab(navigationItem: UserNavigationItem): boolean {
    if (navigationItem.isMenu || !navigationItem.isBaseAccessAllowed) {
      return false;
    }
    return !!this.newTabUrl(navigationItem);
  }

  /**
   * Icoon helemaal rechts op een menu-item (enkel op touch-toestellen, waar
   * geen rechtermuisklik is): opent de pagina in een nieuw tabblad (de gewone
   * klik op het item blijft in dit tabblad navigeren) en sluit het submenu.
   * De klik mag niet doorbubbelen naar het item zelf.
   */
  openInNewTab(navigationItem: UserNavigationItem, event: MouseEvent) {
    event.stopPropagation();
    const url = this.newTabUrl(navigationItem);
    if (!url) {
      return;
    }
    // noopener: de externe pagina krijgt geen window.opener naar de app
    window.open(url, '_blank', 'noopener');
    this.closeSideNav();
  }

  /**
   * Hash-routing: de interne route (met de verplichte navId voor de
   * autorisatie-guard) wordt via de LocationStrategy een `#/...`-URL.
   */
  private newTabUrl(navigationItem: UserNavigationItem): string | undefined {
    if (navigationItem.isExternalPage) {
      // BELANGRIJK: window.open saneert niet; een javascript:-URL zou hier uitvoeren. Daarom
      // dezelfde vertrouwenscheck als de ingebedde weergave (e-external-url).
      const url = navigationItem.config?.url;
      return url && isInTrustedDomain(url) ? url : undefined;
    }
    if (!navigationItem.routerLink) {
      return undefined;
    }
    const tree = this.router.createUrlTree([navigationItem.routerLink], {
      queryParams: { navId: navigationItem.id }
    });
    return this.locationStrategy.prepareExternalUrl(this.router.serializeUrl(tree));
  }

  onNavigationItemClick(navigationItem: UserNavigationItem) {
    if (navigationItem.isMenu || !navigationItem.isBaseAccessAllowed) {
      return;
    }

    // Niet-vertrouwde URL: geen nieuw tabblad maar de gewone pagina, die de melding toont
    const externalTabUrl = navigationItem.isExternalPage && navigationItem.opensInExternalTab
      ? this.newTabUrl(navigationItem)
      : undefined;
    if (externalTabUrl) {
      window.open(externalTabUrl, '_blank', 'noopener');
      this.closeSideNav();
      return;
    }

    this.router.navigate([navigationItem.routerLink], {
      queryParams: {
        navId: navigationItem.id
      }
    });
    this.closeSideNav();
  }
}
